/** Mirrors api/tournament_scoring.py — client-side per-game points for past matches. */

import { isKnockoutGame } from './tournamentCampaign';

const SCORELINE_RE = /^\s*(\d+)\s*:\s*(\d+)\s*$/;

export const GROUP_EXACT_SCORE_POINTS = 10;
export const GROUP_CORRECT_WINNER_POINTS = 5;
export const GROUP_DRAW_ONLY_POINTS = 5;
export const KNOCKOUT_CORRECT_WINNER_POINTS = 15;

export function parsePredictionScoreString(scoreStr) {
  if (!scoreStr || typeof scoreStr !== 'string') return null;
  const m = SCORELINE_RE.exec(scoreStr.trim());
  if (!m) return null;
  return [parseInt(m[1], 10), parseInt(m[2], 10)];
}

export function parseOfficialGoalsFromGame(game) {
  if (!game || typeof game !== 'object') return null;
  const rawA = game.scoreTeamA;
  const rawB = game.scoreTeamB;
  if (rawA == null || rawB == null) return null;
  const a = Number(rawA);
  const b = Number(rawB);
  if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) return null;
  return [a, b];
}

export function getOfficialMatchScoreText(game) {
  const goals = parseOfficialGoalsFromGame(game);
  if (!goals) return null;
  return `${goals[0]} : ${goals[1]}`;
}

function winnerFromScore(score) {
  const [home, away] = score;
  if (home === away) return null;
  return home > away ? 'team1' : 'team2';
}

function predictedWinnerFromEntry(predEntry) {
  if (!predEntry || typeof predEntry !== 'object') return null;
  const winner = predEntry.winner;
  if (winner === 'team1' || winner === 'team2') return winner;
  const pred = parsePredictionScoreString(predEntry.score);
  if (!pred) return null;
  return winnerFromScore(pred);
}

function isExactScoreNumbers(pred, actual) {
  const [pa, pb] = pred;
  const [aa, ab] = actual;
  return (pa === aa && pb === ab) || (pa === ab && pb === aa);
}

export function pointsForGroupStageMatch(pred, actual) {
  const [pa, pb] = pred;
  const [aa, ab] = actual;
  let total = 0;

  if (isExactScoreNumbers(pred, actual)) {
    total += GROUP_EXACT_SCORE_POINTS;
  }

  if (aa === ab) {
    if (pa === pb && !isExactScoreNumbers(pred, actual)) {
      total += GROUP_DRAW_ONLY_POINTS;
    }
    return total;
  }

  if (pa === pb) return total;

  const predWinner = winnerFromScore(pred);
  const actWinner = winnerFromScore(actual);
  if (predWinner != null && predWinner === actWinner) {
    total += GROUP_CORRECT_WINNER_POINTS;
  }

  return total;
}

export function pointsForKnockoutMatch(predEntry, actual) {
  const predWinner = predictedWinnerFromEntry(predEntry);
  if (!predWinner) return 0;
  const actWinner = winnerFromScore(actual);
  if (!actWinner) return 0;
  return predWinner === actWinner ? KNOCKOUT_CORRECT_WINNER_POINTS : 0;
}

export function hasMatchPrediction(game, predEntry) {
  if (!predEntry || typeof predEntry !== 'object') return false;
  if (isKnockoutGame(game)) {
    return predEntry.winner === 'team1' || predEntry.winner === 'team2';
  }
  return Boolean(predEntry.score);
}

export function formatDealerPrediction(game, predEntry) {
  if (!predEntry || typeof predEntry !== 'object') return '-';

  if (isKnockoutGame(game)) {
    const winner = predEntry.winner;
    if (winner === 'team1') {
      return `${game.team1_flag || '🇺🇳'} ${game.team1}`.trim();
    }
    if (winner === 'team2') {
      return `${game.team2_flag || '🇺🇳'} ${game.team2}`.trim();
    }
    return '-';
  }

  const score = predEntry.score;
  if (!score || typeof score !== 'string') return '-';
  const parts = score.split(':');
  if (parts.length !== 2) return score;
  return `${parts[0].trim()} : ${parts[1].trim()}`;
}

/** Points for one past game row; returns null when official score is not set. */
export function pointsForPastGame(game, predEntry) {
  const actual = parseOfficialGoalsFromGame(game);
  if (!actual) return null;

  const entry = predEntry && typeof predEntry === 'object' ? predEntry : {};

  if (isKnockoutGame(game)) {
    return pointsForKnockoutMatch(entry, actual);
  }

  const predRaw = entry.score;
  const pred = predRaw ? parsePredictionScoreString(predRaw) : null;
  if (!pred) return 0;
  return pointsForGroupStageMatch(pred, actual);
}
